"use client"

import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState, useSyncExternalStore } from "react"
import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Menu } from "lucide-react"

import { cn } from "@/lib/utils"
import { sidebarFocusRing, touchTarget, utilityControl } from "@/lib/dashboard-ui"
import {
    dsatSections,
    mentorSections,
    developerSections,
    EASY_MODE_ITEMS,
    type SidebarItem,
    type SidebarSection,
} from "@/lib/dashboard-nav"
import { useAuth } from "@/hooks/use-auth"
import { useSidebarMode } from "@/components/providers/sidebar-mode-provider"

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

/* ── Shared item chrome ──────────────────────────────────────────────────────
 * Everything below is driven by the `--sidebar-*` tokens. Those were already
 * defined for all six themes in globals.css but the sidebar ignored them,
 * hard-coding `text-slate-700 dark:text-slate-200` — which meant the purple,
 * red and custom themes rendered a blue-grey nav that matched nothing.
 */

const ITEM_BASE = cn(
    "relative flex items-center rounded-lg",
    "transition-[background-color,color] duration-150",
    sidebarFocusRing
)

const ITEM_SHAPE = {
    expanded: "gap-3 px-3 h-11 md:h-10 w-full",
    collapsed: "justify-center h-10 w-10 mx-auto",
}

const ITEM_STATE = {
    active: "bg-sidebar-accent text-sidebar-accent-foreground font-semibold",
    idle: "text-sidebar-foreground font-medium hover:bg-sidebar-accent/60",
    disabled: "text-sidebar-foreground/45 cursor-not-allowed select-none",
}

/**
 * Active marker. `aria-current="page"` carries it for screen readers; the rail,
 * the accent fill and the heavier weight carry it visually, so the state never
 * rests on colour alone.
 */
function ActiveRail({ collapsed }: { collapsed: boolean }) {
    return (
        <span
            aria-hidden="true"
            className={cn(
                "absolute rounded-full bg-primary",
                collapsed ? "inset-x-2 -bottom-0.5 h-0.5" : "left-0 top-1/2 h-5 w-1 -translate-y-1/2"
            )}
        />
    )
}

function ItemIcon({ icon: Icon }: { icon: SidebarItem["icon"] }) {
    return <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
}

function NewTag({ label }: { label: string }) {
    return (
        <span className="ml-auto shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-sidebar-accent-foreground">
            {label}
        </span>
    )
}

/** Wraps a collapsed-rail control in a tooltip; expanded items need none. */
function CollapsedLabel({ collapsed, label, children }: { collapsed: boolean; label: string; children: React.ReactNode }) {
    if (!collapsed) return <>{children}</>
    return (
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
    )
}

/* ── Category open/closed state ──────────────────────────────────────────────
 * Read through useSyncExternalStore so the server render and the first client
 * render agree (sections default to open), instead of reading localStorage in
 * an effect and re-rendering every section a frame later.
 */

const categoryListeners = new Set<() => void>()
const categoryKey = (title: string) => `sidebar_cat_${title}`

function subscribeToCategories(onStoreChange: () => void) {
    categoryListeners.add(onStoreChange)
    window.addEventListener("storage", onStoreChange)
    return () => {
        categoryListeners.delete(onStoreChange)
        window.removeEventListener("storage", onStoreChange)
    }
}

/** Snapshot must be referentially stable, so cache the serialised form. */
let categoryCache = { key: "", value: {} as Record<string, boolean> }

function getCategorySnapshot(): Record<string, boolean> {
    const entries: [string, boolean][] = []
    for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i)
        if (storageKey?.startsWith("sidebar_cat_")) {
            entries.push([storageKey.slice("sidebar_cat_".length), localStorage.getItem(storageKey) === "true"])
        }
    }
    entries.sort(([a], [b]) => a.localeCompare(b))
    const serialised = JSON.stringify(entries)
    if (serialised !== categoryCache.key) {
        categoryCache = { key: serialised, value: Object.fromEntries(entries) }
    }
    return categoryCache.value
}

const EMPTY_CATEGORIES: Record<string, boolean> = {}
const getServerCategories = () => EMPTY_CATEGORIES

function setCategoryOpen(title: string, open: boolean) {
    localStorage.setItem(categoryKey(title), String(open))
    categoryListeners.forEach((listener) => listener())
}

/* ── Nav item ────────────────────────────────────────────────────────────── */

interface NavItemProps {
    item: SidebarItem
    collapsed: boolean
    pathname: string | null
    onLinkClick?: () => void
}

function NavItem({ item, collapsed, pathname, onLinkClick }: NavItemProps) {
    const isActive = pathname === item.href
    const shape = collapsed ? ITEM_SHAPE.collapsed : ITEM_SHAPE.expanded

    if (item.disabled) {
        return (
            <li>
                <CollapsedLabel collapsed={collapsed} label={`${item.title} — coming soon`}>
                    <span aria-disabled="true" className={cn(ITEM_BASE, shape, ITEM_STATE.disabled)}>
                        <ItemIcon icon={item.icon} />
                        {!collapsed && (
                            <>
                                <span className="truncate text-base">{item.title}</span>
                                <span className="ml-auto shrink-0 rounded border border-sidebar-border px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide">
                                    Soon
                                </span>
                            </>
                        )}
                    </span>
                </CollapsedLabel>
            </li>
        )
    }

    return (
        <li>
            <CollapsedLabel collapsed={collapsed} label={item.title}>
                <Link
                    href={item.href!}
                    onClick={onLinkClick}
                    // The one signal screen readers actually use for "you are here".
                    aria-current={isActive ? "page" : undefined}
                    // The LMS is another app: it opens in a new tab, signed in through /go-to-lms.
                    {...(item.external ? { target: "_blank", rel: "noreferrer" } : {})}
                    className={cn(ITEM_BASE, shape, isActive ? ITEM_STATE.active : ITEM_STATE.idle)}
                >
                    {isActive && <ActiveRail collapsed={collapsed} />}
                    <ItemIcon icon={item.icon} />
                    {!collapsed && (
                        <>
                            <span className="truncate text-base">{item.title}</span>
                            {item.tag && <NewTag label={item.tag} />}
                            {item.external && (
                                <>
                                    <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
                                    <span className="sr-only">(opens the LMS in a new tab)</span>
                                </>
                            )}
                        </>
                    )}
                </Link>
            </CollapsedLabel>
        </li>
    )
}

/** An item that owns sub-items: a flyout when collapsed, a disclosure when not. */
function NavGroup({ item, collapsed, pathname, onLinkClick }: NavItemProps) {
    const subItems = item.items ?? []
    const isGroupActive = subItems.some((sub) => pathname === sub.href)

    if (collapsed) {
        return (
            <li>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            aria-label={item.title}
                            className={cn(
                                ITEM_BASE,
                                ITEM_SHAPE.collapsed,
                                isGroupActive ? ITEM_STATE.active : ITEM_STATE.idle
                            )}
                        >
                            {isGroupActive && <ActiveRail collapsed />}
                            <ItemIcon icon={item.icon} />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" sideOffset={16} className="w-52">
                        <DropdownMenuLabel className="text-muted-foreground">{item.title}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {subItems.map((sub) => (
                            <DropdownMenuItem key={sub.title} asChild>
                                <Link
                                    href={sub.href!}
                                    onClick={onLinkClick}
                                    aria-current={pathname === sub.href ? "page" : undefined}
                                    className={cn("flex w-full cursor-pointer items-center gap-2", pathname === sub.href && "font-semibold")}
                                >
                                    <ItemIcon icon={sub.icon} />
                                    <span>{sub.title}</span>
                                </Link>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </li>
        )
    }

    return (
        <li>
            <Collapsible defaultOpen={isGroupActive} className="group/collapsible">
                <CollapsibleTrigger
                    className={cn(ITEM_BASE, ITEM_SHAPE.expanded, isGroupActive ? ITEM_STATE.active : ITEM_STATE.idle)}
                >
                    {isGroupActive && <ActiveRail collapsed={false} />}
                    <ItemIcon icon={item.icon} />
                    <span className="truncate text-base">{item.title}</span>
                    <ChevronDown
                        aria-hidden="true"
                        className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 motion-reduce:transition-none"
                    />
                </CollapsibleTrigger>
                <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                    <ul className="mt-1 space-y-1 border-l border-sidebar-border pl-3 ml-5">
                        {subItems.map((sub) => (
                            <li key={sub.title}>
                                <Link
                                    href={sub.href!}
                                    onClick={onLinkClick}
                                    aria-current={pathname === sub.href ? "page" : undefined}
                                    className={cn(
                                        ITEM_BASE,
                                        "h-9 w-full gap-3 px-3 text-sm",
                                        pathname === sub.href ? ITEM_STATE.active : ITEM_STATE.idle
                                    )}
                                >
                                    <ItemIcon icon={sub.icon} />
                                    <span className="truncate">{sub.title}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </CollapsibleContent>
            </Collapsible>
        </li>
    )
}

/* ── Sidebar body ────────────────────────────────────────────────────────── */

interface SidebarContentProps {
    className?: string
    onLinkClick?: () => void
    collapsed?: boolean
    onToggleCollapse?: () => void
}

function SidebarContent({ className, onLinkClick, collapsed = false, onToggleCollapse }: SidebarContentProps) {
    const pathname = usePathname()
    const { user } = useAuth()
    const { isEasyMode } = useSidebarMode()
    const userRole = user?.role ?? 0

    const openCategories = useSyncExternalStore(subscribeToCategories, getCategorySnapshot, getServerCategories)

    const sections: SidebarSection[] = [...dsatSections, ...mentorSections, ...developerSections].filter(
        (section) => !section.minRole || userRole >= section.minRole
    )

    const visibleItems = (section: SidebarSection) =>
        section.items.filter((item) => !item.hidden && (!item.minRole || userRole >= item.minRole))

    // Keep the scroll position across the layout remounts that happen when
    // moving between /dashboard and /admin routes.
    const scrollRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        const saved = sessionStorage.getItem("sidebar_scroll")
        if (saved) el.scrollTop = parseInt(saved, 10)
        const onScroll = () => sessionStorage.setItem("sidebar_scroll", String(el.scrollTop))
        el.addEventListener("scroll", onScroll, { passive: true })
        return () => el.removeEventListener("scroll", onScroll)
    }, [])

    return (
        <TooltipProvider delayDuration={0}>
            <div className={cn("flex h-full flex-col bg-sidebar text-sidebar-foreground", className)}>
                <div
                    className={cn(
                        "flex shrink-0 items-center border-b border-sidebar-border",
                        collapsed ? "h-16 flex-col justify-center gap-2 px-2 py-2" : "h-16 justify-between px-4"
                    )}
                >
                    <Link
                        href="/"
                        onClick={onLinkClick}
                        aria-label="SATBuddy home"
                        className={cn("flex items-center rounded", sidebarFocusRing)}
                    >
                        {/* next/image with explicit dimensions. The old markup was a
                            bare unsized <img> that reserved no space and shifted the
                            rail on load — and the file imported `Image` without ever
                            using it. */}
                        <Image
                            src={collapsed ? "/satbuddy-icon.png" : "/satbuddy-logo.png"}
                            alt="SATBuddy"
                            width={collapsed ? 32 : 128}
                            height={32}
                            priority
                            className={cn("object-contain", collapsed ? "h-8 w-8" : "h-8 w-32 object-left")}
                        />
                    </Link>
                    {onToggleCollapse && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onToggleCollapse}
                            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
                            aria-expanded={!collapsed}
                            aria-controls="sidebar-nav"
                            className={cn("h-8 w-8 shrink-0 text-sidebar-foreground/70 hover:text-sidebar-foreground", sidebarFocusRing)}
                        >
                            {collapsed ? (
                                <ChevronRight className="h-4 w-4" aria-hidden="true" />
                            ) : (
                                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                            )}
                        </Button>
                    )}
                </div>

                <nav
                    id="sidebar-nav"
                    aria-label="Main"
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 scrollbar-thin"
                >
                    {isEasyMode ? (
                        <ul className="space-y-1">
                            {EASY_MODE_ITEMS.map((item) => (
                                <NavItem
                                    key={item.title}
                                    item={item}
                                    collapsed={collapsed}
                                    pathname={pathname}
                                    onLinkClick={onLinkClick}
                                />
                            ))}
                        </ul>
                    ) : (
                        <ul className="space-y-5">
                            {sections.map((section) => {
                                const items = visibleItems(section)
                                if (!items.length) return null

                                // Collapsed rail has no room for headers, so groups stay open.
                                const isOpen = collapsed ? true : openCategories[section.title] ?? true

                                return (
                                    <li key={section.title}>
                                        <Collapsible
                                            open={isOpen}
                                            onOpenChange={(open) => {
                                                if (!collapsed) setCategoryOpen(section.title, open)
                                            }}
                                            className="group/section"
                                        >
                                            {collapsed ? (
                                                <span className="sr-only">{section.title}</span>
                                            ) : (
                                                <CollapsibleTrigger
                                                    className={cn(
                                                        "mb-1 flex h-8 w-full items-center justify-between rounded-lg px-3",
                                                        "text-xs font-semibold uppercase tracking-widest",
                                                        "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                                                        "transition-colors duration-150 cursor-pointer",
                                                        sidebarFocusRing
                                                    )}
                                                >
                                                    <span className="truncate">{section.title}</span>
                                                    <ChevronDown
                                                        aria-hidden="true"
                                                        className="h-4 w-4 shrink-0 opacity-60 transition-transform duration-200 group-data-[state=open]/section:rotate-180 motion-reduce:transition-none"
                                                    />
                                                </CollapsibleTrigger>
                                            )}
                                            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                                                <ul className={cn("space-y-1", collapsed && "flex flex-col items-center")}>
                                                    {items.map((item) =>
                                                        item.items?.length ? (
                                                            <NavGroup
                                                                key={item.title}
                                                                item={item}
                                                                collapsed={collapsed}
                                                                pathname={pathname}
                                                                onLinkClick={onLinkClick}
                                                            />
                                                        ) : (
                                                            <NavItem
                                                                key={item.title}
                                                                item={item}
                                                                collapsed={collapsed}
                                                                pathname={pathname}
                                                                onLinkClick={onLinkClick}
                                                            />
                                                        )
                                                    )}
                                                </ul>
                                            </CollapsibleContent>
                                        </Collapsible>
                                        {collapsed && <div className="my-3 h-px bg-sidebar-border" aria-hidden="true" />}
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                </nav>
            </div>
        </TooltipProvider>
    )
}

/* ── Public shells ───────────────────────────────────────────────────────── */

interface DashboardSidebarProps {
    collapsed: boolean
    onToggle: () => void
}

// A plain div, not <aside>: the <nav aria-label="Main"> inside is the landmark,
// and wrapping it in an unnamed "complementary" region only added a second,
// meaningless stop for screen-reader users.
export function DashboardSidebar({ collapsed, onToggle }: DashboardSidebarProps) {
    return (
        <div
            className={cn(
                "fixed left-0 top-0 z-40 hidden h-[100dvh] border-r border-sidebar-border bg-sidebar md:block",
                "transition-[width] duration-300 motion-reduce:transition-none",
                collapsed ? "w-20" : "w-64"
            )}
        >
            <SidebarContent collapsed={collapsed} onToggleCollapse={onToggle} />
        </div>
    )
}

export function MobileSidebar() {
    const [open, setOpen] = useState(false)

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <button
                    type="button"
                    aria-label="Open navigation menu"
                    className={cn(utilityControl, touchTarget, "h-11 w-11 md:hidden")}
                >
                    <Menu className="h-5 w-5" aria-hidden="true" />
                </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-sidebar-border bg-sidebar p-0">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SidebarContent onLinkClick={() => setOpen(false)} />
            </SheetContent>
        </Sheet>
    )
}
